const RESEARCH_PANEL_IDS = new Set([
  'research-energy',
  'research-focus',
  'research-recovery',
]);

class IngredientResearchTabs extends HTMLElement {
  #tabs = [];
  #panels = [];
  #studyTriggers = [];
  #studyDialogComponents = [];
  #studyDialogComponentsByModalId = new Map();
  #studyOpeners = new WeakMap();
  #carousels = [];
  #carouselResizeObserver;
  #carouselFrame;
  #abortController;
  #scrollFrame;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.#panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.#studyTriggers = Array.from(this.querySelectorAll('[data-research-study-modal-trigger]'));
    this.#studyDialogComponents = Array.from(this.querySelectorAll('[data-research-study-dialog-component]'));
    this.#carousels = Array.from(this.querySelectorAll('[data-research-study-carousel]'))
      .map((root) => ({
        root,
        panel: root.closest('[role="tabpanel"]'),
        track: root.querySelector('[data-research-carousel-track]'),
        previousButton: root.querySelector('[data-research-carousel-previous]'),
        nextButton: root.querySelector('[data-research-carousel-next]'),
      }))
      .filter(({ panel, track, previousButton, nextButton }) => panel && track && previousButton && nextButton);
    this.#studyDialogComponentsByModalId = new Map();
    this.#studyOpeners = new WeakMap();

    this.#studyDialogComponents.forEach((component) => {
      const modalId = component.querySelector('dialog[id]')?.id;
      if (modalId) this.#studyDialogComponentsByModalId.set(modalId, component);
    });

    if (this.#tabs.length === 0 || this.#tabs.length !== this.#panels.length) return;

    this.#tabs.forEach((tab) => {
      tab.addEventListener('click', this.#onTabClick, { signal });
      tab.addEventListener('keydown', this.#onTabKeydown, { signal });
    });

    this.#studyTriggers.forEach((trigger) => {
      trigger.addEventListener('click', this.#onStudyTriggerClick, { signal });
    });

    this.#studyDialogComponents.forEach((component) => {
      component.addEventListener('dialog:close', this.#onStudyDialogClose, { signal });
    });

    this.#carousels.forEach((carousel) => {
      carousel.previousButton.addEventListener('click', () => this.#scrollCarouselByCard(carousel, -1), { signal });
      carousel.nextButton.addEventListener('click', () => this.#scrollCarouselByCard(carousel, 1), { signal });
      carousel.track.addEventListener('scroll', this.#scheduleCarouselControlUpdate, { passive: true, signal });
    });

    this.#carouselResizeObserver?.disconnect();
    if (this.#carousels.length > 0 && 'ResizeObserver' in window) {
      this.#carouselResizeObserver = new ResizeObserver(this.#scheduleCarouselControlUpdate);
      this.#carousels.forEach((carousel) => {
        this.#carouselResizeObserver.observe(carousel.track);
        carousel.track.querySelectorAll('.research-study-card').forEach((card) => {
          this.#carouselResizeObserver.observe(card);
        });
      });
    } else if (this.#carousels.length > 0) {
      window.addEventListener('resize', this.#scheduleCarouselControlUpdate, { signal });
    }

    window.addEventListener('hashchange', this.#onLocationChange, { signal });
    window.addEventListener('popstate', this.#onLocationChange, { signal });

    const hashPanelId = this.#getHashPanelId();
    const initialTab = this.#getTabForPanel(hashPanelId) ?? this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.#tabs[0];

    this.#activateTab(initialTab);

    if (hashPanelId) {
      this.#scheduleSectionScroll();

      if (document.readyState !== 'complete') {
        window.addEventListener('load', this.#scheduleSectionScroll, { once: true, signal });
      }
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#carouselResizeObserver?.disconnect();

    if (this.#carouselFrame) {
      cancelAnimationFrame(this.#carouselFrame);
      this.#carouselFrame = undefined;
    }

    if (this.#scrollFrame) {
      cancelAnimationFrame(this.#scrollFrame);
    }
  }

  #onTabClick = (event) => {
    const tab = event.currentTarget;
    if (!(tab instanceof HTMLButtonElement)) return;

    this.#activateTab(tab, { updateHash: true });
  };

  #onTabKeydown = (event) => {
    const currentIndex = this.#tabs.indexOf(event.currentTarget);
    if (currentIndex < 0) return;

    let nextIndex;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.#tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = this.#tabs[nextIndex];
    this.#activateTab(nextTab, { focus: true, updateHash: true, revealTab: true });
  };

  #onLocationChange = () => {
    const hashPanelId = this.#getHashPanelId();
    if (!hashPanelId) return;

    const tab = this.#getTabForPanel(hashPanelId);
    if (!tab) return;

    this.#activateTab(tab);
    this.#scheduleSectionScroll();
  };

  #onStudyTriggerClick = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLButtonElement)) return;

    const modalId = trigger.getAttribute('aria-controls');
    const dialogComponent = modalId ? this.#studyDialogComponentsByModalId.get(modalId) : undefined;
    if (dialogComponent) this.#studyOpeners.set(dialogComponent, trigger);
  };

  #onStudyDialogClose = (event) => {
    const dialogComponent = event.currentTarget;
    const trigger = this.#studyOpeners.get(dialogComponent);
    this.#studyOpeners.delete(dialogComponent);

    requestAnimationFrame(() => {
      const selectedTab = this.#tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
      const focusTarget = trigger?.isConnected && !trigger.disabled && !trigger.closest('[hidden]') ? trigger : selectedTab;
      focusTarget?.focus({ preventScroll: true });
    });
  };

  #scrollCarouselByCard(carousel, direction) {
    const { track } = carousel;
    const cards = Array.from(track?.querySelectorAll('.research-study-card') ?? []);
    if (!track || cards.length === 0) return;

    const styles = getComputedStyle(track);
    const trackStart = track.getBoundingClientRect().left;
    const scrollPadding =
      Number.parseFloat(styles.scrollPaddingInlineStart) || Number.parseFloat(styles.paddingInlineStart) || 0;
    const alignmentPoint = trackStart + scrollPadding;
    const currentIndex = cards.reduce((closestIndex, card, index) => {
      const currentDistance = Math.abs(cards[closestIndex].getBoundingClientRect().left - alignmentPoint);
      const cardDistance = Math.abs(card.getBoundingClientRect().left - alignmentPoint);
      return cardDistance < currentDistance ? index : closestIndex;
    }, 0);
    const targetIndex = Math.max(0, Math.min(cards.length - 1, currentIndex + direction));
    const targetCard = cards[targetIndex];
    const targetScrollLeft = track.scrollLeft + targetCard.getBoundingClientRect().left - alignmentPoint;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollTo({
      left: targetScrollLeft,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  }

  #scheduleCarouselControlUpdate = () => {
    if (this.#carouselFrame) return;

    this.#carouselFrame = requestAnimationFrame(() => {
      this.#updateCarouselControls();
      this.#carouselFrame = undefined;
    });
  };

  #updateCarouselControls() {
    this.#carousels.forEach(({ panel, track, previousButton, nextButton }) => {
      if (panel.hidden) return;

      const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
      const scrollLeft = Math.max(0, track.scrollLeft);
      const hasOverflow = maxScrollLeft > 3;

      previousButton.disabled = !hasOverflow || scrollLeft <= 3;
      nextButton.disabled = !hasOverflow || scrollLeft >= maxScrollLeft - 3;
    });
  }

  #activateTab(tab, { focus = false, updateHash = false, revealTab = false } = {}) {
    const panelId = tab?.getAttribute('aria-controls');
    const panel = this.#panels.find((item) => item.id === panelId);
    if (!tab || !panel) return;

    const activePanel = this.#panels.find((item) => !item.hidden);
    const focusWasInActivePanel = activePanel?.contains(document.activeElement) ?? false;

    this.#tabs.forEach((item) => {
      const isSelected = item === tab;
      item.setAttribute('aria-selected', String(isSelected));
      item.tabIndex = isSelected ? 0 : -1;
    });

    this.#panels.forEach((item) => {
      item.hidden = item !== panel;
    });

    if (activePanel && activePanel !== panel) {
      this.#studyDialogComponents.forEach((component) => {
        if (component.querySelector('dialog')?.open) component.closeDialog?.();
      });
    }

    if (panel.querySelector('[data-research-study-carousel]')) {
      this.#scheduleCarouselControlUpdate();
    }

    if (focus || focusWasInActivePanel) {
      tab.focus({ preventScroll: true });
    }

    if (revealTab) {
      tab.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    }

    if (updateHash && window.location.hash !== `#${panelId}`) {
      const url = new URL(window.location.href);
      url.hash = panelId;
      window.history.replaceState(window.history.state, '', url);
    }
  }

  #getHashPanelId() {
    if (!window.location.hash) return null;

    try {
      const id = decodeURIComponent(window.location.hash.slice(1));
      return RESEARCH_PANEL_IDS.has(id) ? id : null;
    } catch {
      return null;
    }
  }

  #getTabForPanel(panelId) {
    if (!panelId) return null;
    return this.#tabs.find((tab) => tab.getAttribute('aria-controls') === panelId) ?? null;
  }

  #scheduleSectionScroll = () => {
    if (this.#scrollFrame) cancelAnimationFrame(this.#scrollFrame);

    this.#scrollFrame = requestAnimationFrame(() => {
      this.scrollIntoView({ behavior: 'auto', block: 'start' });
      this.#scrollFrame = undefined;
    });
  };
}

if (!customElements.get('ingredient-research-tabs')) {
  customElements.define('ingredient-research-tabs', IngredientResearchTabs);
}
